import { Badge } from '@/components/ui/badge'
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor
} from '@/components/ui/combobox'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

import type { Option } from './filter-state'

type Props<Value extends string> = {
  /** field name. inline: rendered as the placeholder; block: a label above. */
  label: string
  options: Option<Value>[]
  selected: Value[]
  onSelectedChange: (next: Value[]) => void
  /** inline = compact toolbar field (no wrap, +N overflow, label as the
   *  placeholder); block = full-width sheet field (label above, chips wrap). */
  variant?: 'inline' | 'block'
  /** block-variant input placeholder; defaults to the label. */
  placeholder?: string
  /** inline only: chips shown before the rest collapse into a +N badge. */
  maxVisible?: number
  className?: string
}

/** multi-select facet filter shared by the directory toolbar (inline) and the
 * filter sheet (block). lists every canonical option since filtering is
 * server-side. both variants bind to the same SupplierFilterState, so the
 * toolbar and sheet stay in sync.
 */
export function FacetCombobox<Value extends string>({
  label,
  options,
  selected,
  onSelectedChange,
  variant = 'inline',
  placeholder,
  maxVisible = 2,
  className
}: Props<Value>) {
  const anchor = useComboboxAnchor()
  const selectedOptions = options.filter(option =>
    selected.includes(option.value)
  )
  const inline = variant === 'inline'
  /** inline caps the chips and collapses the rest to +N; block shows them all
   *  and wraps. */
  const visible = inline
    ? selectedOptions.slice(0, maxVisible)
    : selectedOptions
  const overflow = selectedOptions.length - visible.length

  const combobox = (
    <Combobox
      multiple
      items={options}
      value={selectedOptions}
      onValueChange={(next: Option<Value>[]) =>
        onSelectedChange(next.map(option => option.value))
      }
      itemToStringLabel={(option: Option<Value>) => option.label}
      itemToStringValue={(option: Option<Value>) => option.label}
    >
      <ComboboxChips
        ref={anchor}
        className={cn(
          inline &&
            'w-full flex-nowrap overflow-hidden sm:w-auto sm:max-w-[18rem] sm:min-w-[11rem]',
          className
        )}
      >
        {visible.map(option => (
          <ComboboxChip
            key={option.value}
            /** shrinkable so a long value compresses instead of pushing the
             *  +N badge and the input out of a fixed-width trigger. */
            className={inline ? 'min-w-0 shrink max-w-[8rem]' : undefined}
          >
            {inline ? (
              <span className='truncate'>{option.label}</span>
            ) : (
              option.label
            )}
          </ComboboxChip>
        ))}
        {overflow > 0 && (
          <Badge
            variant='secondary'
            className='h-[calc(--spacing(4.75))] shrink-0 px-1.5 text-xs font-medium'
          >
            +{overflow}
          </Badge>
        )}
        <ComboboxChipsInput
          className={inline ? 'min-w-8 shrink-0' : undefined}
          placeholder={
            selectedOptions.length > 0
              ? ''
              : inline
                ? label
                : (placeholder ?? label)
          }
        />
      </ComboboxChips>
      <ComboboxContent anchor={anchor}>
        <ComboboxEmpty>No matches.</ComboboxEmpty>
        <ComboboxList>
          {(option: Option<Value>) => (
            <ComboboxItem key={option.value} value={option}>
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )

  if (inline) return combobox

  return (
    <div className='flex flex-col gap-2'>
      <Label className='text-xs font-medium text-foreground'>{label}</Label>
      {combobox}
    </div>
  )
}
